// DataModelTree.tsx — LAZY-LOAD REWRITE
//
// THE FIX
// ───────────────────────────────────────────────────────────────────────
// getDMProfile(hydrate=20) returns ALL 47 sub-entities but only 20 are
// hydrated (have fields). The other 27 are header-only (hydrated:false).
//
// The old code overlaid lazyProfile on top of the DM tree nodes — but
// sub-entities 21-47 simply don't exist as DM tree nodes at all (they
// were never in the pre-loaded refinedDM), so they were invisible.
//
// THE AUTHORITATIVE CHILD SOURCE:
//   When lazyProfile is loaded, lazyProfile.sub_entities IS the child
//   list for the root node. We render all N sub-entities from there,
//   not from node.field.
//
//   hydrated=true  → render its fields normally
//   hydrated=false → render a "Load fields ▾" button
//   has_more=true  → render a "Load N more" button after the field list
//
// ROOT STATS: sum lazyProfile.sub_entities[].total  (= server total = 685)
// GROUP STATS: from sub.stats (server rollup over ALL fields, not loaded)
// FIELD COUNT BADGE: sub.total  (not fields.length)
//
// All business logic, drag-drop, save handlers — 100% unchanged.
// ───────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faMinus, faFile, faRightLeft, faList, faSitemap,
  faChevronRight, faChevronDown, faGripVertical, faSpinner,
  faAngleDoubleDown,
} from '@fortawesome/free-solid-svg-icons';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

import {
  textFieldIconData, integerFieldIconData, dropdownFieldIconData,
  decimalFieldIconData, dateFieldIconData, dateTimeFieldIconData,
  updateFieldDeep, generateRandomString, findField,
  convertToRawDm, DQ_RULE_CAT_LABELS, attachProfileToDM,
} from './constants';

import {
  getDMProfile, getDMProfilePage,
  DMProfile, DMProfileSubEntity,
  LAZY_CONFIG,
} from './API';
import { getMatchRules } from './mdmAPI';

import FieldPropAddUpdate from './FieldPropAddUpdateLazy';
import FieldGrpPropAddUpdate from './FieldGrpPropAddUpdate';
import AddFieldModal from './AddFieldModal';
import DMTreeGridSummary from './ddm/DMTreeGridSummaryLazy';

// ─── Palette ─────────────────────────────────────────────────────────────────
const T = {
  bg:'#eef1f6', bgCard:'#ffffff', bgSidebar:'#f5f7fa',
  textPrimary:'#0f1923', textSecond:'#1e2d3d', textMuted:'#445566', textLabel:'#2c3e52',
  brand:'#1e3a6e', brandLight:'#dde7f5', brandMid:'#90aed0', border:'#c8d4e0',
  green:'#166534', greenBg:'#f0fdf4', greenBorder:'#86efac',
  amber:'#7c2d00', amberBg:'#fff7ed', amberBorder:'#fde68a',
  red:'#991b1b', redBg:'#fef2f2', redBorder:'#fca5a5',
  purple:'#6b21a8', purpleBg:'#faf5ff', purpleBorder:'#d8b4fe',
};

// ─── CSS ──────────────────────────────────────────────────────────────────────
const INJECT_STYLE = `
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600&family=Sora:wght@300;400;600;700&display=swap');
.dmt-root { background:${T.bg}; font-family:'Sora',system-ui,sans-serif; color:${T.textPrimary}; min-height:100vh; }

.dmt-toolbar { display:flex; align-items:center; justify-content:space-between; padding:0 16px; height:44px; background:${T.bgCard}; border-bottom:2px solid ${T.border}; gap:8px; flex-wrap:nowrap; overflow:hidden; }
.dmt-toolbar-left { display:flex; align-items:center; gap:6px; flex:1; min-width:0; overflow:hidden; }
.dmt-toolbar-right { display:flex; align-items:center; gap:6px; flex-shrink:0; }

.dmt-toggle-btn { display:inline-flex; align-items:center; gap:6px; padding:5px 12px; border-radius:7px; font-size:11.5px; font-weight:600; cursor:pointer; border:1px solid ${T.border}; background:${T.bgCard}; color:${T.brand}; font-family:'Sora',system-ui,sans-serif; transition:all .15s; white-space:nowrap; }
.dmt-toggle-btn:hover { background:${T.brandLight}; border-color:${T.brandMid}; }

.dmt-action-btn { width:30px; height:30px; border-radius:7px; display:inline-flex; align-items:center; justify-content:center; border:1px solid ${T.border}; background:${T.bgCard}; cursor:pointer; color:${T.textMuted}; transition:all .15s; }
.dmt-action-btn:hover { background:${T.brandLight}; border-color:${T.brandMid}; color:${T.brand}; }

.dmt-filter-cat-label { font-family:'JetBrains Mono',monospace; font-size:9px; font-weight:600; letter-spacing:.14em; text-transform:uppercase; color:${T.textMuted}; white-space:nowrap; flex-shrink:0; }
.dmt-filter-pill { display:inline-flex; align-items:center; gap:4px; padding:3px 9px; border-radius:14px; font-size:11px; font-weight:600; cursor:pointer; border:1px solid ${T.border}; background:${T.bgCard}; color:${T.textMuted}; transition:all .15s; white-space:nowrap; flex-shrink:0; }
.dmt-filter-pill:hover { border-color:${T.brandMid}; color:${T.brand}; background:${T.brandLight}; }
.dmt-filter-pill.active { background:${T.brand}; border-color:${T.brand}; color:#fff; }
.dmt-filter-pill .pill-count { font-size:9.5px; font-weight:700; background:rgba(255,255,255,.25); border-radius:8px; padding:0 5px; }
.dmt-filter-pill:not(.active) .pill-count { background:${T.bgSidebar}; color:${T.textMuted}; }
.dmt-more-btn { display:inline-flex; align-items:center; gap:5px; padding:3px 10px; border-radius:14px; font-size:11px; font-weight:600; cursor:pointer; border:1px solid ${T.border}; background:${T.bgCard}; color:${T.textMuted}; transition:all .15s; white-space:nowrap; flex-shrink:0; }
.dmt-more-btn:hover { border-color:${T.brandMid}; color:${T.brand}; background:${T.brandLight}; }
.dmt-more-btn.has-active { background:${T.brandLight}; border-color:${T.brandMid}; color:${T.brand}; }
.dmt-more-num { font-size:9px; background:${T.brandLight}; color:${T.brand}; padding:1px 5px; border-radius:99px; font-weight:700; }
.dmt-over-drop { position:absolute; top:calc(100% + 4px); left:0; z-index:500; background:#fff; border:1px solid ${T.border}; border-radius:9px; box-shadow:0 10px 30px rgba(0,0,0,.12); min-width:180px; overflow:hidden; }
.dmt-over-row { display:flex; align-items:center; gap:8px; padding:8px 14px; font-size:12px; cursor:pointer; color:${T.textSecond}; transition:background .1s; }
.dmt-over-row:hover { background:${T.bgSidebar}; }
.dmt-over-row.active { background:${T.brandLight}; color:${T.brand}; font-weight:600; }

.dmt-body { display:flex; height:calc(100vh - 88px); overflow:hidden; }
.dmt-left { width:400px; min-width:220px; max-width:440px; background:${T.bgSidebar}; border-right:1px solid ${T.border}; display:flex; flex-direction:column; overflow:hidden; }
.dmt-left-header { padding:10px 16px 9px; border-bottom:1px solid ${T.border}; display:flex; align-items:center; justify-content:space-between; }
.dmt-left-header-title { font-family:'JetBrains Mono',monospace; font-size:10px; font-weight:600; letter-spacing:.14em; text-transform:uppercase; color:${T.textMuted}; }
.dmt-left-scroll { flex:1; overflow-y:auto; padding:10px 8px; }
.dmt-left-scroll::-webkit-scrollbar { width:4px; }
.dmt-left-scroll::-webkit-scrollbar-thumb { background:${T.border}; border-radius:2px; }
.dmt-left-actions { padding:10px 12px; border-top:1px solid ${T.border}; display:flex; gap:6px; background:${T.bgCard}; }
.dmt-right { flex:1; overflow-y:auto; background:${T.bg}; padding:20px 24px; }
.dmt-right::-webkit-scrollbar { width:4px; }
.dmt-right::-webkit-scrollbar-thumb { background:${T.border}; border-radius:2px; }

.dmt-node { margin-bottom:3px; border-radius:8px; transition:background .12s; cursor:pointer; }
.dmt-node-row { display:flex; align-items:center; gap:6px; padding:7px 10px; border-radius:8px; transition:background .12s,box-shadow .12s; position:relative; }
.dmt-node-row:hover { background:${T.brandLight}; }
.dmt-node-row.selected { background:${T.brandLight}; box-shadow:inset 3px 0 0 ${T.brand}; }
.dmt-node-expand-btn { width:18px; height:18px; border-radius:4px; border:none; background:none; cursor:pointer; color:${T.textMuted}; display:flex; align-items:center; justify-content:center; flex-shrink:0; font-size:10px; transition:color .12s; }
.dmt-node-expand-btn:hover { color:${T.brand}; }
.dmt-node-icon { width:18px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.dmt-node-label { font-size:12px; font-weight:600; color:${T.textPrimary}; flex:1; min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.dmt-node-label.group { color:${T.brand}; font-size:11px; font-weight:700; letter-spacing:.02em; }
.dmt-node-meta { font-size:10px; color:${T.textMuted}; }
.dmt-node-badges { display:flex; flex-direction:column; align-items:flex-end; gap:2px; flex-shrink:0; }
.dmt-node-children { padding-left:20px; }
.dmt-drag-handle { color:${T.border}; font-size:10px; cursor:grab; flex-shrink:0; }
.dmt-badge { font-size:9px; font-weight:700; padding:1px 6px; border-radius:8px; font-family:'JetBrains Mono',monospace; letter-spacing:.04em; white-space:nowrap; }
.dmt-badge.green  { background:${T.greenBg};  color:${T.green};  border:1px solid ${T.greenBorder}; }
.dmt-badge.amber  { background:${T.amberBg};  color:${T.amber};  border:1px solid ${T.amberBorder}; }
.dmt-badge.red    { background:${T.redBg};    color:${T.red};    border:1px solid ${T.redBorder}; }
.dmt-badge.brand  { background:${T.brandLight}; color:${T.brand}; border:1px solid ${T.brandMid}; }
.dmt-badge.muted  { background:${T.bgSidebar}; color:${T.textMuted}; border:1px solid ${T.border}; }
.dmt-badge.purple { background:${T.purpleBg}; color:${T.purple}; border:1px solid ${T.purpleBorder}; }
.dmt-kpi-strip { display:flex; flex-wrap:wrap; gap:4px; padding:6px 10px 8px 28px; }
.dmt-kpi-chip { display:inline-flex; align-items:center; gap:4px; font-family:'JetBrains Mono',monospace; font-size:9px; font-weight:600; padding:2px 8px; border-radius:10px; cursor:pointer; transition:all .12s; border:1px solid transparent; }
.dmt-kpi-chip.c-cols   { background:${T.bgSidebar}; color:${T.textMuted}; border-color:${T.border}; cursor:default; }
.dmt-kpi-chip.c-null   { background:${T.redBg};    color:${T.red};    border-color:${T.redBorder}; }
.dmt-kpi-chip.c-null.active    { background:${T.red};    color:#fff; }
.dmt-kpi-chip.c-single { background:${T.amberBg};  color:${T.amber};  border-color:${T.amberBorder}; }
.dmt-kpi-chip.c-single.active  { background:${T.amber};  color:#fff; }
.dmt-kpi-chip.c-two    { background:${T.greenBg};  color:${T.green};  border-color:${T.greenBorder}; }
.dmt-kpi-chip.c-two.active     { background:${T.green};  color:#fff; }
.dmt-kpi-chip.c-multi  { background:${T.purpleBg}; color:${T.purple}; border-color:${T.purpleBorder}; }
.dmt-kpi-chip.c-multi.active   { background:${T.purple}; color:#fff; }
.dmt-kpi-chip.c-cavg   { background:${T.brandLight}; color:${T.brand}; border-color:${T.brandMid}; cursor:default; }
.dmt-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; height:60%; color:${T.textMuted}; text-align:center; }
.dmt-empty-icon { font-size:40px; margin-bottom:12px; opacity:.3; }
.dmt-empty-label { font-size:13px; font-weight:300; }

/* lazy-load UI */
.dmt-load-btn { display:inline-flex; align-items:center; gap:5px; margin:3px 0 3px 0; padding:4px 12px; border-radius:8px; font-size:10px; font-weight:600; font-family:'JetBrains Mono',monospace; cursor:pointer; border:1px dashed ${T.brandMid}; background:${T.brandLight}; color:${T.brand}; transition:all .15s; white-space:nowrap; }
.dmt-load-btn:hover:not(:disabled) { background:${T.brand}; color:#fff; border-style:solid; }
.dmt-load-btn:disabled { opacity:.5; cursor:default; }
.dmt-load-more-wrap { padding:5px 10px 5px 10px; }
.dmt-unhydrated-row { display:flex; align-items:center; gap:6px; padding:7px 10px; border-radius:8px; }
.dmt-spin { display:inline-block; animation:dmt-spin 1s linear infinite; }
@keyframes dmt-spin { to { transform:rotate(360deg); } }
.dmt-skel-line { height:10px; border-radius:4px; background:linear-gradient(90deg,${T.border} 25%,${T.brandLight} 50%,${T.border} 75%); background-size:200% 100%; animation:dmt-shimmer 1.4s ease infinite; }
@keyframes dmt-shimmer { to { background-position:-200% 0; } }
`;

// ─── Interfaces ───────────────────────────────────────────────────────────────
interface DataModelTreeProps {
  selectedApp: any;
  tool: string;
  dataModelData: any;
  srcSystems: any;
  selectedDMEntityId: string | null;
  dataSrcSystemId?: string | null;
  supportingData?: Record<string, any>;
  handleUpdateDMData: (data: any) => void;
  handleOpenDataModelTreeModal: () => void;
  appNm?: string;
}

interface DataModelTreeState {
  expandedNodes: Set<string>;
  dataModelData: any;
  filteredDataModelData: any;
  selectedData: any;
  showAddFieldModal: boolean;
  treeSummaryView: boolean;
  showEditFieldModal: boolean;
  groupFilters: Record<string, string | null>;
  // lazy
  lazyProfile: DMProfile | null;
  lazyLoading: boolean;
  subLoadingSet: Set<string>;   // sub-entity names being hydrated
  fieldLoadingSet: Set<string>; // sub-entity names with "load more" in flight
  // match rules for the selected entity root (populated lazily from lazyProfile / dataModelData)
  matchRules: any[];
  matchRulesLoading: boolean;
}

// ─── helpers ──────────────────────────────────────────────────────────────────
const filterDataModelByColumns = (node: any, colNames: Set<string>): any | null => {
  if (node.eClass === 'Field') {
    const parts = node.FLD_PHYSICAL_ID?.split('-');
    const key = parts && parts.length > 1 ? parts[1] : null;
    return key && colNames.has(key) ? { ...node } : null;
  }
  if (node.eClass === 'FieldGroup' && Array.isArray(node.field)) {
    const kids = node.field.map((c: any) => filterDataModelByColumns(c, colNames)).filter(Boolean);
    if (kids.length > 0) return { ...node, field: kids };
  }
  return null;
};

const buildFilters = (profilingResults: any[], rawDM: any[]) => {
  const allowedTags = ['should_be_lookup'];
  const categories: any = {
    modeling: { label: 'Modeling', subcategories: {} },
    rules:    { label: 'Rules',    subcategories: {} },
  };
  profilingResults.forEach(profile => {
    const insight = profile.insight || {};
    const dmCol = rawDM.find((c: any) => {
      const parts = c.FLD_PHYSICAL_ID?.split('-');
      const key = parts && parts.length > 1 ? parts[1] : null;
      return key === profile.column_name;
    });
    [...(insight.modeling_recommendations || [])].forEach((item: any) => {
      const tag = item.tag || 'other';
      if (!allowedTags.includes(tag)) return;
      if (!categories.modeling.subcategories[tag])
        categories.modeling.subcategories[tag] = { label: tag, totalCount: 0, items: [], approvedCount: 0 };
      categories.modeling.subcategories[tag].totalCount++;
      if (tag === 'should_be_lookup' && dmCol?.DATA_TYP === 'LookupField')
        categories.modeling.subcategories[tag].approvedCount++;
      categories.modeling.subcategories[tag].items.push(profile);
    });
    (insight.validation_rules || []).forEach((rule: any) => {
      const catKey = rule.cat || 'other';
      const catLabel = DQ_RULE_CAT_LABELS[catKey] || catKey;
      if (!categories.rules.subcategories[catKey])
        categories.rules.subcategories[catKey] = { label: catLabel, totalCount: 0, approvedCount: 0, items: [] };
      categories.rules.subcategories[catKey].totalCount++;
      if (rule.lifecycle === 'Approved') categories.rules.subcategories[catKey].approvedCount++;
      categories.rules.subcategories[catKey].items.push(profile);
    });
  });
  return categories;
};

// ─── FilterBar (unchanged) ────────────────────────────────────────────────────
const MAX_VISIBLE_PILLS = 6;
const FilterBar = ({ profilingResults, rawDM, onFilter }: any) => {
  const categories = buildFilters(profilingResults, rawDM);
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined);
  const [overOpen,  setOverOpen]  = useState(false);
  const overRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (overRef.current && !overRef.current.contains(e.target as Node)) setOverOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const allPills: any[] = [];
  Object.entries(categories).filter(([_,cv]: any) => Object.keys(cv.subcategories).length > 0)
    .forEach(([catKey, catValue]: any) => {
      Object.entries(catValue.subcategories).forEach(([subKey, subValue]: any) => {
        const count = subValue.approvedCount !== undefined ? `${subValue.approvedCount}/${subValue.totalCount}` : String(subValue.totalCount);
        allPills.push({ key:`${catKey}-${subKey}`, catLabel:catValue.label, subLabel:subValue.label, count, items:subValue.items });
      });
    });
  if (allPills.length === 0) return null;
  const visible = allPills.slice(0, MAX_VISIBLE_PILLS);
  const overflow = allPills.slice(MAX_VISIBLE_PILLS);
  const overflowHasActive = overflow.some((p:any) => p.key === activeKey);
  const activate = (key: string, items: any[]) => {
    const next = activeKey === key ? undefined : key;
    setActiveKey(next);
    onFilter(next ? items : profilingResults);
    setOverOpen(false);
  };
  let lastCat = '';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:5, minWidth:0, overflow:'hidden' }}>
      {visible.map((pill:any) => {
        const showCat = pill.catLabel !== lastCat; lastCat = pill.catLabel;
        const isActive = activeKey === pill.key;
        return (
          <React.Fragment key={pill.key}>
            {showCat && <span className="dmt-filter-cat-label">{pill.catLabel}:</span>}
            <button className={`dmt-filter-pill${isActive?' active':''}`} onClick={() => activate(pill.key, pill.items)}>
              {pill.subLabel}<span className="pill-count">{pill.count}</span>
            </button>
          </React.Fragment>
        );
      })}
      {overflow.length > 0 && (
        <div ref={overRef} style={{ position:'relative', flexShrink:0 }}>
          <button className={`dmt-more-btn${overflowHasActive?' has-active':''}`} onClick={() => setOverOpen(o=>!o)}>
            {overflowHasActive ? overflow.find((p:any)=>p.key===activeKey)?.subLabel ?? 'More' : 'More'}
            <span className="dmt-more-num">{overflow.length}</span>
            <span style={{fontSize:8}}>▾</span>
          </button>
          {overOpen && (
            <div className="dmt-over-drop">
              {overflow.map((pill:any) => {
                const isActive = activeKey === pill.key;
                return (
                  <div key={pill.key} className={`dmt-over-row${isActive?' active':''}`} onClick={() => activate(pill.key, pill.items)}>
                    <span style={{flex:1}}>{pill.catLabel} · {pill.subLabel}</span>
                    <span style={{fontSize:10,fontFamily:'monospace',fontWeight:700,color:isActive?T.brand:T.textMuted}}>{pill.count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
class DataModelTree extends React.Component<DataModelTreeProps, DataModelTreeState> {

  private processAndSortData(props: DataModelTreeProps, currentDMEntityId: string | null): any[] {
    let dm = props.dataModelData.filter((e: any) => e.id === currentDMEntityId);
    if (props.supportingData?.DDM_PROCESS_DATA && dm.length > 0) {
      const eData = (props.supportingData ?? {})['DDM_PROCESS_DATA'][dm[0]['entityId']];
      if (eData?.data_profiling_results?.profiling_results)
        dm = attachProfileToDM(dm, eData.data_profiling_results.profiling_results, true);
    }
    return dm;
  }

  constructor(props: DataModelTreeProps) {
    super(props);
    const initialDm = this.processAndSortData(props, props.selectedDMEntityId);
    const firstNodeId = initialDm.length > 0 ? (initialDm[0].id || '') : '';
    this.state = {
      expandedNodes: new Set<string>([firstNodeId]),
      dataModelData: initialDm,
      filteredDataModelData: initialDm,
      selectedData: initialDm.length > 0 ? initialDm[0] : null,
      showAddFieldModal: false,
      treeSummaryView: props.tool !== 'BUILD_DG_USING_DATA' && props.dataSrcSystemId !== 'CANONICAL',
      showEditFieldModal: false,
      groupFilters: {},
      lazyProfile: null,
      lazyLoading: false,
      subLoadingSet: new Set(),
      fieldLoadingSet: new Set(),
      matchRules: [],
      matchRulesLoading: false,
    };
  }

  // ── lazy profile fetch ──────────────────────────────────────────────────────
  private _loadLazyProfile(appNm: string, entityId: string) {
    this.setState({ lazyLoading: true, lazyProfile: null });
    getDMProfile(appNm, entityId, LAZY_CONFIG.PAGE_SIZE, LAZY_CONFIG.HYDRATE_SUBENTITIES)
      .then(profile => this.setState({ lazyProfile: profile, lazyLoading: false }))
      .catch(err => { console.warn('[DMT] getDMProfile failed', err); this.setState({ lazyLoading: false }); });
  }

  componentDidMount() {
    const { appNm, selectedDMEntityId } = this.props;
    if (appNm && selectedDMEntityId) this._loadLazyProfile(appNm, selectedDMEntityId);
  }

  componentDidUpdate(prevProps: DataModelTreeProps) {
    const entityChanged = prevProps.selectedDMEntityId !== this.props.selectedDMEntityId;
    const dataChanged   = prevProps.dataModelData !== this.props.dataModelData;
    if (entityChanged || dataChanged) {
      const nextDm = this.processAndSortData(this.props, this.props.selectedDMEntityId);
      this.setState({
        dataModelData: nextDm, filteredDataModelData: nextDm,
        expandedNodes: new Set<string>([nextDm.length > 0 ? (nextDm[0].id || '') : '']),
        selectedData: nextDm.length > 0 ? nextDm[0] : null,
        treeSummaryView: this.props.tool !== 'BUILD_DG_USING_DATA' && this.props.dataSrcSystemId !== 'CANONICAL',
        lazyProfile: null, lazyLoading: false,
        subLoadingSet: new Set(), fieldLoadingSet: new Set(),
        matchRules: [], matchRulesLoading: false,
      });
      if (entityChanged && this.props.appNm && this.props.selectedDMEntityId)
        this._loadLazyProfile(this.props.appNm, this.props.selectedDMEntityId);
    } else if (!this.state.selectedData && this.props.dataModelData.filter((e:any) => e.id === this.props.selectedDMEntityId).length > 0) {
      const dm = this.props.dataModelData.filter((e:any) => e.id === this.props.selectedDMEntityId);
      this.setState({ selectedData: dm?.[0] ?? null, treeSummaryView: this.props.tool !== 'BUILD_DG_USING_DATA' && this.props.dataSrcSystemId !== 'CANONICAL' });
    }
  }

  // ── hydrate a header-only sub-entity ───────────────────────────────────────
  private _hydrateSubEntity(subName: string) {
    const { appNm, selectedDMEntityId } = this.props;
    if (!appNm || !selectedDMEntityId) return;
    this.setState(prev => { const s = new Set(prev.subLoadingSet); s.add(subName); return { subLoadingSet: s }; });
    getDMProfilePage(appNm, selectedDMEntityId, subName, 0, LAZY_CONFIG.PAGE_SIZE)
      .then(page => {
        this.setState(prev => {
          if (!prev.lazyProfile) return null;
          const subs = prev.lazyProfile.sub_entities.map(se =>
            se.name !== subName ? se : { ...se, hydrated:true, fields:page.fields, loaded:page.fields.length, total:page.total, has_more:page.has_more }
          );
          const s = new Set(prev.subLoadingSet); s.delete(subName);
          return { lazyProfile:{ ...prev.lazyProfile, sub_entities:subs }, subLoadingSet:s };
        });
      })
      .catch(err => {
        console.warn('[DMT] hydrateSubEntity failed', err);
        this.setState(prev => { const s = new Set(prev.subLoadingSet); s.delete(subName); return { subLoadingSet:s }; });
      });
  }

  // ── load more fields for a hydrated sub-entity ─────────────────────────────
  private _loadMoreFields(subName: string, offset: number) {
    const { appNm, selectedDMEntityId } = this.props;
    if (!appNm || !selectedDMEntityId) return;
    this.setState(prev => { const s = new Set(prev.fieldLoadingSet); s.add(subName); return { fieldLoadingSet:s }; });
    getDMProfilePage(appNm, selectedDMEntityId, subName, offset, LAZY_CONFIG.PAGE_SIZE)
      .then(page => {
        this.setState(prev => {
          if (!prev.lazyProfile) return null;
          const subs = prev.lazyProfile.sub_entities.map(se =>
            se.name !== subName ? se : { ...se, fields:[...se.fields,...page.fields], loaded:se.loaded+page.fields.length, has_more:page.has_more }
          );
          const s = new Set(prev.fieldLoadingSet); s.delete(subName);
          return { lazyProfile:{ ...prev.lazyProfile, sub_entities:subs }, fieldLoadingSet:s };
        });
      })
      .catch(err => {
        console.warn('[DMT] loadMoreFields failed', err);
        this.setState(prev => { const s = new Set(prev.fieldLoadingSet); s.delete(subName); return { fieldLoadingSet:s }; });
      });
  }

  // ── unchanged helpers ──────────────────────────────────────────────────────
  getFieldIcon = (dataType?: string) => {
    const map: Record<string,any> = { TextField:textFieldIconData, IntegerField:integerFieldIconData, LookupField:dropdownFieldIconData, DateField:dateFieldIconData, DateTimeField:dateTimeFieldIconData, DecimalField:decimalFieldIconData };
    const src = dataType && map[dataType];
    if (src) return <img src={src} alt="" style={{height:11,opacity:.7}} />;
    return <FontAwesomeIcon icon={faFile} style={{fontSize:10,color:T.textMuted}} />;
  };
  toggleExpand = (id: string) => {
    this.setState(prev => { const n = new Set(prev.expandedNodes); n.has(id)?n.delete(id):n.add(id); return {expandedNodes:n}; });
  };
  // Populate match rules when the user selects a FieldGroup node.
  // Priority:
  //   1. Node already carries matchRules (non-lazy path / previously cached)
  //   2. dataModelData entry has them (build/save flow pre-loaded the full DM)
  //   3. Lazy path: call getMatchRules(appNm, entityId) from mdmAPI
  _resolveMatchRulesForSelection(selectedData: any) {
    if (!selectedData) { this.setState({ matchRules: [], matchRulesLoading: false }); return; }

    // Only entity-root FieldGroup nodes carry matchRules.
    // Sub-entity groups (have FLD_ID) and leaf fields never need them.
    if (selectedData.eClass !== 'FieldGroup' || selectedData.FLD_ID) return;

    // 1. Already on the node (non-lazy path or previously fetched + cached)
    if (Array.isArray(selectedData.matchRules) && selectedData.matchRules.length > 0) {
      this.setState({ matchRules: selectedData.matchRules, matchRulesLoading: false });
      return;
    }

    // 2. Check dataModelData (full refinedDM entry pre-loaded by build/save flow)
    const dmEntry = (this.state.dataModelData || []).find(
      (e: any) => e.id === selectedData.id || e.entityId === selectedData.entityId
    );
    if (dmEntry?.matchRules?.length > 0) {
      this.setState({ matchRules: dmEntry.matchRules, matchRulesLoading: false });
      return;
    }

    // 3. Lazy path — fetch from the API.
    // entityId comes from selectedData.entityId (root node) or selectedDMEntityId prop.
    const entityId = selectedData.entityId || this.props.selectedDMEntityId;
    const appNm    = this.props.appNm;
    if (!appNm || !entityId) {
      this.setState({ matchRules: [], matchRulesLoading: false });
      return;
    }

    this.setState({ matchRules: [], matchRulesLoading: true });
    getMatchRules(appNm, entityId)
      .then(res => {
        const rules = res?.match_rules ?? [];
        // Cache onto the node so re-selection doesn't re-fetch
        selectedData.matchRules = rules;
        this.setState({ matchRules: rules, matchRulesLoading: false });
      })
      .catch(err => {
        console.warn('[DataModelTree] getMatchRules failed', err);
        this.setState({ matchRules: [], matchRulesLoading: false });
      });
  }

  handleFieldSelected = (selectedData: any) => {
    this.setState({ selectedData });
    this._resolveMatchRulesForSelection(selectedData);
  };
  handleSaveFieldPropAddUpdateData = (fldData: any) => {
    const id = fldData.FLD_ID;
    if (id) {
      const dm = this.state.dataModelData;
      dm.forEach((a:any, idx:number) => { const m = findField(a,id); if (m&&m.p) m.p.field[m.p.field.indexOf(m.c)]=fldData; else if (m&&!m.p) dm[idx]=fldData; });
      this.setState({dataModelData:dm}); this.props.handleUpdateDMData(dm);
    }
  };
  handleSaveFieldGrpPropAddUpdateData = (fldData: any) => {
    const id = fldData.id;
    if (id) {
      const dm = this.state.dataModelData;
      fldData.field.filter((x:any)=>x.eClass==='Field').forEach((a:any)=>{a.TABLE_NM=fldData.name;a.TABLE_DISPLAY_NM=fldData.label;});
      dm.forEach((a:any,idx:number)=>{const m=findField(a,id);if(m&&m.p)m.p.field[m.p.field.indexOf(m.c)]=fldData;else if(m&&!m.p)dm[idx]=fldData;});
      this.setState({dataModelData:dm}); this.props.handleUpdateDMData(dm);
    }
  };
  toggleAddFieldModal = () => this.setState(prev=>({showAddFieldModal:!prev.showAddFieldModal}));
  handleAddField = () => { if (this.state.selectedData?.eClass==='FieldGroup') this.toggleAddFieldModal(); else alert('Select root or any nested attribute'); };
  handleSaveAddField = (data: any) => {
    const dm = this.state.dataModelData; const sel = this.state.selectedData;
    const fld:any = data.nested
      ? {eClass:'FieldGroup',name:data.name,label:data.label,id:'CFG_'+generateRandomString(8),field:[]}
      : {TBL_ID:sel.id,FLD_ID:sel.id+'-CF_'+generateRandomString(8),TABLE_NM:sel.name,TABLE_DISPLAY_NM:sel.label,PARENT_TABLE_NM:'',COLUMN_NAME:data.name,COLUMN_DISPLAY_NAME:data.label,SEQ:0,DATA_TYP:'TextField',DATA_DEFAULT:null,MANDATORY:'N',DATA_LEN:'20',LOOKUP_RDS_NM:null,LOOKUP_RDS_DISP_NM:null,LOOKUP_RDS_CV:null,TABLE_DATA:0,COLUMN_DATA:[],SYSTEM_ORDER:'',eClass:'Field',SYSTEM_ORDER_GUID:'',SYSTEM_ORDER_CD:''};
    dm.forEach((a:any)=>{const m=findField(a,sel.id);if(m?.c){const fg=m.c.field.filter((f:any)=>f.eClass==='FieldGroup');fg.length>0?m.c.field.splice(m.c.field.indexOf(fg[0]),0,fld):m.c.field.push(fld);}});
    this.setState({dataModelData:dm,selectedData:fld}); this.props.handleUpdateDMData(dm); this.toggleAddFieldModal();
  };
  handleRemoveField = () => {
    const {dataModelData,selectedData} = this.state;
    if (!selectedData) return;
    const remove = () => {
      const id = selectedData.id||selectedData.FLD_ID;
      dataModelData.forEach((a:any)=>{const m=findField(a,id);if(m?.p){m.p.field.splice(m.p.field.indexOf(m.c),1);this.setState({selectedData:m.p});}});
      this.setState({dataModelData}); this.props.handleUpdateDMData(dataModelData);
    };
    confirmAlert({ title:'Confirm Delete', message:'Are you sure you want to delete this item?', closeOnEscape:false, closeOnClickOutside:false, buttons:[{label:'Yes',onClick:remove},{label:'No',onClick:()=>{}}] });
  };
  onDragEnd = (result: DropResult) => {
    const {destination,source} = result;
    if (!destination||destination.index===source.index) return;
    const removeNode=(tree:any[],nodeId:string):any|null=>{for(let i=0;i<tree.length;i++){if(tree[i].id===nodeId||tree[i].FLD_ID===nodeId)return tree.splice(i,1)[0];if(tree[i].field){const r=removeNode(tree[i].field,nodeId);if(r)return r;}}return null;};
    const insertNode=(tree:any[],parentId:string,idx:number,node:any):boolean=>{for(let i=0;i<tree.length;i++){if(tree[i].id===parentId){const items=Array.from(tree[i].field);items.splice(idx,0,node);tree[i].field=items;return true;}if(tree[i].field){const ins=insertNode(tree[i].field,parentId,idx,node);if(ins)return true;}}return false;};
    const newDM=JSON.parse(JSON.stringify(this.state.dataModelData));
    const dragged=removeNode(newDM,result.draggableId);
    if(dragged)insertNode(newDM,destination.droppableId,destination.index,dragged);
    this.setState({dataModelData:newDM}); this.props.handleUpdateDMData(newDM);
  };
  flattenFields = (node: any): any[] => { if(!node.field?.length)return[];let all=[...node.field];node.field.forEach((ch:any)=>{all=[...all,...this.flattenFields(ch)];});return all; };
  groupStats = (node: any) => {
    const fields=this.flattenFields(node); if(!fields.length)return null;
    let nullCols=0,single=0,two=0,multi=0,totalComp=0,cComp=0;
    fields.forEach(f=>{if(!f.profile)return;const{null_percentage,unique_values}=f.profile;if(null_percentage===100)nullCols++;if(unique_values===1)single++;if(unique_values===2)two++;if(unique_values>2)multi++;if(null_percentage!=null){totalComp+=(100-null_percentage);cComp++;}});
    return {total:fields.length,nullCols,single,two,multi,avgComp:cComp>0?totalComp/cComp:null};
  };

  // ── render a LEAF field node ───────────────────────────────────────────────
  renderFieldNode = (node: any, index: number) => {
    // Ensure a stable id: lazy fields from getDMProfile always have FLD_ID,
    // but fall back to TBL_ID+COLUMN_NAME in case the backend omits it.
    const id = node.FLD_ID || node.id || `${node.TBL_ID||''}-${node.COLUMN_NAME||''}`;
    // Back-fill FLD_ID onto the node so handleFieldSelected stores a matchable key
    if (!node.FLD_ID && id) node.FLD_ID = id;
    const label = node.COLUMN_DISPLAY_NAME || node.label;
    const selectedId = this.state.selectedData
      ? (this.state.selectedData.FLD_ID || this.state.selectedData.id || '')
      : '';
    const isSelected = !!id && id === selectedId;
    const completeness = node.profile ? (100-(node.profile.null_percentage??100)) : null;
    const validRules = node.profile?.insight?.validation_rules;
    const validResults = node.profile?.insight?.validation_results;
    let validBadge: React.ReactNode = null;
    if (Array.isArray(validRules) && Array.isArray(validResults)) {
      const approved = validRules.filter((r:any)=>(r.lifecycle||'').toLowerCase()==='approved');
      const approvedIds = new Set(approved.map((r:any)=>r.id));
      const approvedRes = validResults.filter((r:any)=>approvedIds.has(r.rule_id));
      const passed = approvedRes.filter((r:any)=>r.status?.toUpperCase()==='PASS').length;
      const failed = approvedRes.filter((r:any)=>r.status?.toUpperCase()==='FAIL').length;
      const total = approvedRes.length;
      if (total>0) { const cls=failed===0?'green':failed/total<0.5?'amber':'red'; validBadge=<span className={`dmt-badge ${cls}`}>V {passed}/{total}</span>; }
    }
    return (
      <div key={id} className="dmt-node">
        <Draggable draggableId={id} index={index}>
          {provided => (
            <div ref={provided.innerRef} {...provided.draggableProps}>
              <div className={`dmt-node-row${isSelected?' selected':''}`} onClick={() => this.handleFieldSelected(node)}>
                <span {...provided.dragHandleProps} className="dmt-drag-handle" onClick={e=>e.stopPropagation()}><FontAwesomeIcon icon={faGripVertical}/></span>
                <button className="dmt-node-expand-btn" />
                <span className="dmt-node-icon">{this.getFieldIcon(node.DATA_TYP)}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div className="dmt-node-label">{label}</div>
                  {node.profile?.orig_column_name && <div className="dmt-node-meta">{node.profile.orig_column_name}</div>}
                </div>
                <div className="dmt-node-badges">
                  {completeness!==null && <span className={`dmt-badge ${completeness>90?'green':'amber'}`}>C {completeness.toFixed(0)}%</span>}
                  {validBadge}
                </div>
              </div>
            </div>
          )}
        </Draggable>
      </div>
    );
  };

  // ── render a SUB-ENTITY group from lazyProfile ─────────────────────────────
  // This is the KEY FIX: renders ALL sub-entities from lazyProfile, not from node.field.
  renderLazySubEntity = (se: DMProfileSubEntity, index: number) => {
    const id = `lazy-${se.name}`;   // stable drag ID for this sub-entity
    const isExpanded = this.state.expandedNodes.has(id);
    const activeFilter = this.state.groupFilters[id] || null;
    const isHydrating = this.state.subLoadingSet.has(se.name);
    const isLoadingMore = this.state.fieldLoadingSet.has(se.name);
    const selectedId = this.state.selectedData ? (this.state.selectedData.id||this.state.selectedData.FLD_ID) : '';
    const isSelected = id === selectedId;

    // Stats: always from server (se.stats covers ALL fields, not just loaded)
    const stats = se.stats;

    const handleKpiClick = (key: string) => {
      this.setState(prev => ({ groupFilters:{ ...prev.groupFilters, [id]: prev.groupFilters[id]===key?null:key } }));
    };

    // Ensure every lazy field has a stable FLD_ID before rendering
    se.fields.forEach((f: any) => {
      if (!f.FLD_ID && (f.TBL_ID || f.COLUMN_NAME)) {
        f.FLD_ID = `${f.TBL_ID || se.name}-${f.COLUMN_NAME || ''}`;
      }
      // Ensure eClass is set so the right panel recognises them as fields
      if (!f.eClass) f.eClass = 'Field';
    });

    // Apply client-side filter over the LOADED fields only
    let visibleFields = se.fields;
    if (activeFilter && se.hydrated) {
      visibleFields = se.fields.filter((f:any) => {
        const { null_percentage, unique_values } = f.profile || {};
        switch (activeFilter) {
          case 'nullCols':    return null_percentage === 100;
          case 'singleValue': return unique_values === 1;
          case 'twoValue':    return unique_values === 2;
          case 'multiValue':  return unique_values > 2;
          default:            return true;
        }
      });
    }

    // Synthetic node object so existing save handlers can work with it
    const syntheticNode = { id, name: se.name, label: se.label, eClass: 'FieldGroup', field: se.fields };

    return (
      <div key={id} className="dmt-node">
        <Draggable draggableId={id} index={index}>
          {provided => (
            <div ref={provided.innerRef} {...provided.draggableProps}>
              {/* ── Group header row ── */}
              <div className={`dmt-node-row${isSelected?' selected':''}`} onClick={() => this.handleFieldSelected(syntheticNode)}>
                <span {...provided.dragHandleProps} className="dmt-drag-handle" onClick={e=>e.stopPropagation()}><FontAwesomeIcon icon={faGripVertical}/></span>
                <button className="dmt-node-expand-btn" onClick={e=>{e.stopPropagation();this.toggleExpand(id);}}>
                  <FontAwesomeIcon icon={isExpanded?faChevronDown:faChevronRight}/>
                </button>
                <span className="dmt-node-icon"><FontAwesomeIcon icon={faFile} style={{fontSize:10,color:T.brand}}/></span>
                <div style={{flex:1,minWidth:0}}>
                  <div className="dmt-node-label group">{se.label || se.name}</div>
                </div>
                {/* Field count badge — ALWAYS from server total */}
                <span
                  className="dmt-badge brand"
                  title={se.loaded < se.total ? `${se.loaded} loaded of ${se.total} total` : `${se.total} fields`}
                  style={{marginRight:2}}
                >
                  {se.loaded < se.total ? `${se.loaded}/${se.total}` : se.total} cols
                </span>
              </div>

              {/* ── KPI chips — from server stats ── */}
              {stats && (
                <div className="dmt-kpi-strip">
                  <span className="dmt-kpi-chip c-cols">{se.total} total</span>
                  <span className={`dmt-kpi-chip c-null${activeFilter==='nullCols'?' active':''}`} onClick={e=>{e.stopPropagation();handleKpiClick('nullCols');}}>Null {stats.nullCols}</span>
                  <span className={`dmt-kpi-chip c-single${activeFilter==='singleValue'?' active':''}`} onClick={e=>{e.stopPropagation();handleKpiClick('singleValue');}}>1-Val {stats.single}</span>
                  <span className={`dmt-kpi-chip c-two${activeFilter==='twoValue'?' active':''}`} onClick={e=>{e.stopPropagation();handleKpiClick('twoValue');}}>2-Val {stats.two}</span>
                  <span className={`dmt-kpi-chip c-multi${activeFilter==='multiValue'?' active':''}`} onClick={e=>{e.stopPropagation();handleKpiClick('multiValue');}}>Multi {stats.multi}</span>
                  {stats.avgComp!=null && <span className="dmt-kpi-chip c-cavg">C̄ {stats.avgComp.toFixed(0)}%</span>}
                </div>
              )}
            </div>
          )}
        </Draggable>

        {/* ── Expanded body ── */}
        {isExpanded && (
          <div className="dmt-node-children">
            {/* NOT yet hydrated */}
            {!se.hydrated && (
              <div style={{padding:'4px 10px 8px'}}>
                {isHydrating ? (
                  <div style={{display:'flex',flexDirection:'column',gap:4,padding:'4px 0'}}>
                    {[80,65,75].map((w,i)=><div key={i} className="dmt-skel-line" style={{width:`${w}%`}}/>)}
                  </div>
                ) : (
                  <button className="dmt-load-btn" onClick={e=>{e.stopPropagation();this._hydrateSubEntity(se.name);}}>
                    <FontAwesomeIcon icon={faAngleDoubleDown} style={{fontSize:9}}/>
                    Load {se.total} fields
                  </button>
                )}
              </div>
            )}

            {/* Hydrated — render fields */}
            {se.hydrated && (
              <>
                <Droppable droppableId={id} type="root">
                  {provided1 => (
                    <div {...provided1.droppableProps} ref={provided1.innerRef}>
                      {visibleFields.map((f:any, i:number) => this.renderFieldNode(f, i))}
                      {provided1.placeholder}
                    </div>
                  )}
                </Droppable>

                {activeFilter && visibleFields.length===0 && (
                  <div style={{fontSize:11,color:T.textMuted,padding:'6px 10px'}}>No fields match filter</div>
                )}

                {/* Load more fields — show when has_more OR loaded < total (defensive) */}
                {(se.has_more || se.loaded < se.total) && (
                  <div className="dmt-load-more-wrap">
                    <button
                      className="dmt-load-btn"
                      disabled={isLoadingMore}
                      onClick={e=>{e.stopPropagation();this._loadMoreFields(se.name,se.loaded);}}
                    >
                      {isLoadingMore
                        ? <><span className="dmt-spin"><FontAwesomeIcon icon={faSpinner} style={{fontSize:9}}/></span> Loading…</>
                        : <><FontAwesomeIcon icon={faAngleDoubleDown} style={{fontSize:9}}/> Load {Math.min(LAZY_CONFIG.PAGE_SIZE, se.total-se.loaded)} more ({se.loaded}/{se.total})</>
                      }
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── render the ROOT node (wraps lazyProfile sub-entities as children) ───────
  renderRootNode = (node: any, index: number) => {
    const id = node.id || node.FLD_ID;
    const label = node.label || node.COLUMN_DISPLAY_NAME;
    const isExpanded = this.state.expandedNodes.has(id);
    const selectedId = this.state.selectedData ? (this.state.selectedData.id||this.state.selectedData.FLD_ID) : '';
    const isSelected = id === selectedId;
    const { lazyProfile, lazyLoading } = this.state;

    // Root stats: sum lazyProfile sub-entity totals — the REAL field count
    let rootStats: any = null;
    if (lazyProfile) {
      const totalCols = lazyProfile.sub_entities.reduce((acc, se) => acc + se.total, 0);
      // Aggregate stats from all sub-entities that have stats
      let nullCols=0, single=0, two=0, multi=0, compSum=0, compCount=0;
      lazyProfile.sub_entities.forEach(se => {
        if (se.stats) {
          nullCols += se.stats.nullCols;
          single   += se.stats.single;
          two      += se.stats.two;
          multi    += se.stats.multi;
          if (se.stats.avgComp != null) { compSum += se.stats.avgComp * se.total; compCount += se.total; }
        }
      });
      rootStats = { total: totalCols, nullCols, single, two, multi, avgComp: compCount > 0 ? compSum/compCount : null };
    } else if (!lazyLoading) {
      // Fall back to client calc from DM tree if lazy hasn't loaded
      rootStats = this.groupStats(node);
    }

    // Use lazyProfile sub-entities as children when available
    const useLazy = !!lazyProfile;
    const lazyChildren = lazyProfile?.sub_entities ?? [];
    // Fallback: children from DM tree (pre-lazy or when lazy fails)
    const dmChildren: any[] = node.field || [];

    return (
      <div key={id} className="dmt-node">
        <Draggable draggableId={id} index={index}>
          {provided => (
            <div ref={provided.innerRef} {...provided.draggableProps}>
              <div className={`dmt-node-row${isSelected?' selected':''}`} onClick={() => this.handleFieldSelected(node)}>
                <span {...provided.dragHandleProps} className="dmt-drag-handle" onClick={e=>e.stopPropagation()}><FontAwesomeIcon icon={faGripVertical}/></span>
                <button className="dmt-node-expand-btn" onClick={e=>{e.stopPropagation();this.toggleExpand(id);}}>
                  <FontAwesomeIcon icon={isExpanded?faChevronDown:faChevronRight}/>
                </button>
                <span className="dmt-node-icon"><FontAwesomeIcon icon={faFile} style={{fontSize:10,color:T.brand}}/></span>
                <div style={{flex:1,minWidth:0}}>
                  <div className="dmt-node-label group">{label}</div>
                </div>
                {/* Root field count from server */}
                {rootStats && (
                  <span className="dmt-badge brand" style={{marginRight:2}}>
                    {rootStats.total} cols
                  </span>
                )}
                {lazyLoading && (
                  <span className="dmt-spin" style={{marginRight:6}}>
                    <FontAwesomeIcon icon={faSpinner} style={{fontSize:10,color:T.textMuted}}/>
                  </span>
                )}
              </div>

              {/* Root KPI strip */}
              {rootStats && (
                <div className="dmt-kpi-strip">
                  <span className="dmt-kpi-chip c-cols">
                    {useLazy ? `${lazyChildren.length} tables` : `${rootStats.total} cols`}
                  </span>
                  <span className="dmt-kpi-chip c-null">Null {rootStats.nullCols}</span>
                  <span className="dmt-kpi-chip c-single">1-Val {rootStats.single}</span>
                  <span className="dmt-kpi-chip c-two">2-Val {rootStats.two}</span>
                  <span className="dmt-kpi-chip c-multi">Multi {rootStats.multi}</span>
                  {rootStats.avgComp!=null && <span className="dmt-kpi-chip c-cavg">C̄ {rootStats.avgComp.toFixed(0)}%</span>}
                </div>
              )}
            </div>
          )}
        </Draggable>

        {/* ── Root children ── */}
        {isExpanded && (
          <div className="dmt-node-children">
            {/* Lazy skeleton while profile loads */}
            {lazyLoading && (
              <div style={{padding:'8px 10px',display:'flex',flexDirection:'column',gap:6}}>
                {[75,85,65,80,70].map((w,i)=><div key={i} className="dmt-skel-line" style={{width:`${w}%`,height:13}}/>)}
              </div>
            )}

            {/* LAZY PATH: render ALL sub-entities from lazyProfile */}
            {useLazy && (
              <DragDropContext onDragEnd={this.onDragEnd}>
                <Droppable droppableId={id} type="root">
                  {provided => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {lazyChildren.map((se, i) => this.renderLazySubEntity(se, i))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}

            {/* FALLBACK PATH: DM tree nodes (before lazy loads or if lazy fails) */}
            {!useLazy && !lazyLoading && (
              <DragDropContext onDragEnd={this.onDragEnd}>
                <Droppable droppableId={id} type="root">
                  {provided => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {dmChildren.map((child: any, i: number) => this.renderNodeList(child, i))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── renderNodeList: for DM-tree nodes (fallback path / non-root groups) ─────
  renderNodeList = (node: any, index: number) => {
    const id = node.id || node.FLD_ID;
    const label = node.label || node.COLUMN_DISPLAY_NAME;
    const isExpanded = this.state.expandedNodes.has(id);
    const hasChildren = Array.isArray(node.field) && node.field.length > 0;
    const selectedId = this.state.selectedData ? (this.state.selectedData.id||this.state.selectedData.FLD_ID) : '';
    const isSelected = id === selectedId;
    const completeness = node.profile ? (100-(node.profile.null_percentage??100)) : null;
    const activeFilter = this.state.groupFilters[id] || null;
    const stats = hasChildren ? this.groupStats(node) : null;
    let children = node.field || [];
    if (activeFilter && hasChildren) {
      children = children.filter((f:any)=>{
        const{null_percentage,unique_values}=f.profile||{};
        switch(activeFilter){case'nullCols':return null_percentage===100;case'singleValue':return unique_values===1;case'twoValue':return unique_values===2;case'multiValue':return unique_values>2;default:return true;}
      });
    }
    const handleKpiClick = (key:string)=>this.setState(prev=>({groupFilters:{...prev.groupFilters,[id]:prev.groupFilters[id]===key?null:key}}));
    return (
      <div key={id} className="dmt-node">
        <Draggable draggableId={id} index={index}>
          {provided => (
            <div ref={provided.innerRef} {...provided.draggableProps}>
              <div className={`dmt-node-row${isSelected?' selected':''}`} onClick={()=>this.handleFieldSelected(node)}>
                <span {...provided.dragHandleProps} className="dmt-drag-handle" onClick={e=>e.stopPropagation()}><FontAwesomeIcon icon={faGripVertical}/></span>
                <button className="dmt-node-expand-btn" onClick={e=>{e.stopPropagation();if(hasChildren)this.toggleExpand(id);}}>
                  {hasChildren?<FontAwesomeIcon icon={isExpanded?faChevronDown:faChevronRight}/>:null}
                </button>
                <span className="dmt-node-icon">{this.getFieldIcon(node.DATA_TYP)}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div className={`dmt-node-label${hasChildren?' group':''}`}>{label}</div>
                  {node.profile?.orig_column_name&&<div className="dmt-node-meta">{node.profile.orig_column_name}</div>}
                </div>
                {!hasChildren&&(
                  <div className="dmt-node-badges">
                    {completeness!==null&&<span className={`dmt-badge ${completeness>90?'green':'amber'}`}>C {completeness.toFixed(0)}%</span>}
                  </div>
                )}
                {hasChildren&&stats&&<span className="dmt-badge brand" style={{marginRight:2}}>{stats.total} cols</span>}
              </div>
              {hasChildren&&stats&&(
                <div className="dmt-kpi-strip">
                  <span className="dmt-kpi-chip c-cols">{stats.total} total</span>
                  <span className={`dmt-kpi-chip c-null${activeFilter==='nullCols'?' active':''}`} onClick={e=>{e.stopPropagation();handleKpiClick('nullCols');}}>Null {stats.nullCols}</span>
                  <span className={`dmt-kpi-chip c-single${activeFilter==='singleValue'?' active':''}`} onClick={e=>{e.stopPropagation();handleKpiClick('singleValue');}}>1-Val {stats.single}</span>
                  <span className={`dmt-kpi-chip c-two${activeFilter==='twoValue'?' active':''}`} onClick={e=>{e.stopPropagation();handleKpiClick('twoValue');}}>2-Val {stats.two}</span>
                  <span className={`dmt-kpi-chip c-multi${activeFilter==='multiValue'?' active':''}`} onClick={e=>{e.stopPropagation();handleKpiClick('multiValue');}}>Multi {stats.multi}</span>
                  {stats.avgComp!=null&&<span className="dmt-kpi-chip c-cavg">C̄ {stats.avgComp.toFixed(0)}%</span>}
                </div>
              )}
            </div>
          )}
        </Draggable>
        {isExpanded&&hasChildren&&(
          <div className="dmt-node-children">
            <Droppable droppableId={id} type="root">
              {provided1=>(
                <div {...provided1.droppableProps} ref={provided1.innerRef}>
                  {children.map(this.renderNodeList)}
                  {provided1.placeholder}
                </div>
              )}
            </Droppable>
            {activeFilter&&children.length===0&&<div style={{fontSize:11,color:T.textMuted,padding:'6px 10px'}}>No fields match filter</div>}
          </div>
        )}
      </div>
    );
  };

  renderNode = (node: any, index: number) => {
    const id=node.id||node.FLD_ID;const label=node.label||node.COLUMN_DISPLAY_NAME;
    const isExpanded=this.state.expandedNodes.has(id);const hasChildren=node.field&&node.field.length>0;
    const selectedId=this.state.selectedData?(this.state.selectedData.id||this.state.selectedData.FLD_ID):'';const isSelected=id===selectedId;
    return(
      <div key={id} className="dmt-node">
        <Draggable draggableId={id} index={index}>
          {provided=>(
            <div ref={provided.innerRef} {...provided.draggableProps}>
              <div className={`dmt-node-row${isSelected?' selected':''}`} onClick={()=>this.handleFieldSelected(node)}>
                <span {...provided.dragHandleProps} className="dmt-drag-handle" onClick={e=>e.stopPropagation()}><FontAwesomeIcon icon={faGripVertical}/></span>
                <button className="dmt-node-expand-btn" onClick={e=>{e.stopPropagation();if(hasChildren)this.toggleExpand(id);}}>
                  {hasChildren?<FontAwesomeIcon icon={isExpanded?faChevronDown:faChevronRight}/>:null}
                </button>
                <span className="dmt-node-icon">{this.getFieldIcon(node.DATA_TYP)}</span>
                <div style={{flex:1,minWidth:0}}><div className={`dmt-node-label${hasChildren?' group':''}`}>{label}</div></div>
              </div>
            </div>
          )}
        </Draggable>
        {isExpanded&&hasChildren&&(
          <div className="dmt-node-children">
            <Droppable droppableId={id} type="root">
              {provided1=>(
                <div {...provided1.droppableProps} ref={provided1.innerRef}>
                  {node.field.map(this.renderNode)}
                  {provided1.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        )}
      </div>
    );
  };

  handleOpenFieldEditModal  = (selectedData: any) => this.setState({selectedData,showEditFieldModal:true});
  handleCloseFieldEditModal = () => this.setState({showEditFieldModal:false});

  renderEditModal = (dmWithProfile: any, eDataSupportingData: any) => (
    <Modal show={this.state.showEditFieldModal} onHide={this.handleCloseFieldEditModal} size="xl">
      <Modal.Header closeButton style={{borderBottom:`1px solid ${T.border}`}}>
        <Modal.Title style={{fontSize:16,fontWeight:700,color:T.textPrimary}}>Edit Field</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{background:T.bg}}>
        {this.state.selectedData?.eClass==='Field'
          ? <FieldPropAddUpdate selectedApp={this.props.selectedApp} tool={this.props.tool} dataModelData={dmWithProfile} handleSave={data=>{this.handleSaveFieldPropAddUpdateData(data);this.handleCloseFieldEditModal();}} initialData={this.state.selectedData} supportingData={eDataSupportingData} srcSystems={this.props.srcSystems}/>
          : <FieldGrpPropAddUpdate tool={this.props.tool} handleSave={data=>{this.handleSaveFieldGrpPropAddUpdateData(data);this.handleCloseFieldEditModal();}} initialData={this.state.selectedData} srcSystems={this.props.srcSystems} matchRules={this.state.matchRules} matchRulesLoading={this.state.matchRulesLoading}/>
        }
      </Modal.Body>
    </Modal>
  );

  render() {
    const rootId = this.state.dataModelData?.[0]?.id || 'droppable';
    const importedBE = this.state.dataModelData?.[0]?.imported || false;
    let eDataSupportingData: any = null;
    let dmWithProfile = this.state.dataModelData;
    const rawDM = this.props.selectedDMEntityId && convertToRawDm(this.state.dataModelData)[this.props.selectedDMEntityId];
    if (this.props.supportingData?.DDM_PROCESS_DATA && this.state.dataModelData.length>0 && this.props.selectedDMEntityId) {
      eDataSupportingData = (this.props.supportingData ?? {}).DDM_PROCESS_DATA[this.state.dataModelData[0].entityId];
      dmWithProfile = attachProfileToDM(dmWithProfile, eDataSupportingData?.data_profiling_results?.profiling_results);
    }
    const metadataMap = rawDM && new Map(rawDM.map((m:any)=>{const parts=m.FLD_PHYSICAL_ID?.split('-');const key=parts&&parts.length>1?parts[1]:null;return[key,m];}));
    const colProfiles = metadataMap && eDataSupportingData?.data_profiling_results?.profiling_results.filter((p:any)=>metadataMap.has(p.column_name));

    if (this.state.treeSummaryView) {
      return (
        <div className="dmt-root">
          <style>{INJECT_STYLE}</style>
          <div className="dmt-toolbar">
            <div className="dmt-toolbar-left"/>
            <div className="dmt-toolbar-right">
              <button className="dmt-toggle-btn" onClick={()=>this.setState({treeSummaryView:false})}><FontAwesomeIcon icon={faSitemap}/> Tree View</button>
            </div>
          </div>
          <div style={{padding:'0 0 20px'}}>
            <DMTreeGridSummary
              selectedApp={this.props.selectedApp}
              tool={this.props.tool}
              dm={this.state.dataModelData?.[0] ?? this.props.dataModelData?.[0]}
              entityId={this.props.selectedDMEntityId ?? ''}
              appNm={this.props.appNm ?? ''}
              onEditFieldClick={this.handleOpenFieldEditModal}
            />
          </div>
          {this.renderEditModal(dmWithProfile[0], eDataSupportingData)}
        </div>
      );
    }

    const isDQ = true;

    return (
      <div className="dmt-root">
        <style>{INJECT_STYLE}</style>
        <div className="dmt-toolbar">
          <div className="dmt-toolbar-left">
            {colProfiles && rawDM && (
              <FilterBar profilingResults={colProfiles} rawDM={rawDM} onFilter={(filteredItems:any[])=>{
                const colNames:Set<string>=new Set(filteredItems.map((f:any)=>String(f.column_name)));
                const filtered=filterDataModelByColumns(this.state.dataModelData[0],colNames);
                this.setState({filteredDataModelData:filtered?[filtered]:[],expandedNodes:new Set<string>(filtered?[filtered.id]:''),selectedData:filtered||null});
              }}/>
            )}
          </div>
          <div className="dmt-toolbar-right">
            <button className="dmt-toggle-btn" onClick={()=>this.setState({treeSummaryView:true})}><FontAwesomeIcon icon={faList}/> Summary View</button>
          </div>
        </div>

        <div className="dmt-body">
          <div className="dmt-left">
            <div className="dmt-left-header">
              <span className="dmt-left-header-title">Data Model</span>
              {this.state.lazyProfile && (
                <span className="dmt-badge brand">
                  {this.state.lazyProfile.sub_entities.length} tables · {this.state.lazyProfile.sub_entities.reduce((a,s)=>a+s.total,0)} fields
                </span>
              )}
            </div>
            <div className="dmt-left-scroll">
              {/* The root Droppable wraps the root-level items */}
              <DragDropContext onDragEnd={this.onDragEnd}>
                <Droppable droppableId={rootId} type="nested">
                  {provided => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {isDQ
                        ? this.state.filteredDataModelData.map((node:any, i:number) => this.renderRootNode(node, i))
                        : this.state.filteredDataModelData.map(this.renderNode)
                      }
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
            <div className="dmt-left-actions">
              {!importedBE && (
                <button className="dmt-action-btn" title="Rearrange" onClick={this.props.handleOpenDataModelTreeModal}><FontAwesomeIcon icon={faRightLeft} style={{fontSize:11}}/></button>
              )}
              <button className="dmt-action-btn" title="Add field" onClick={this.handleAddField}><FontAwesomeIcon icon={faPlus} style={{fontSize:11}}/></button>
              <button className="dmt-action-btn" title="Remove field" onClick={this.handleRemoveField}><FontAwesomeIcon icon={faMinus} style={{fontSize:11}}/></button>
            </div>
          </div>

          <div className="dmt-right">
            {/* A node is a Field if eClass='Field' OR it has a FLD_ID (lazy fields may skip eClass) */}
            {this.state.selectedData && (this.state.selectedData.eClass==='Field' || (this.state.selectedData.FLD_ID && this.state.selectedData.eClass!=='FieldGroup')) && (
              <FieldPropAddUpdate
                selectedApp={this.props.selectedApp}
                tool={this.props.tool}
                dataModelData={dmWithProfile[0] ?? this.state.dataModelData?.[0] ?? {}}
                handleSave={this.handleSaveFieldPropAddUpdateData}
                initialData={this.state.selectedData}
                supportingData={eDataSupportingData}
                srcSystems={this.props.srcSystems}
              />
            )}
            {this.state.selectedData && (this.state.selectedData.eClass==='FieldGroup' || (!this.state.selectedData.FLD_ID && !this.state.selectedData.eClass)) && (
              <FieldGrpPropAddUpdate
                tool={this.props.tool}
                handleSave={this.handleSaveFieldGrpPropAddUpdateData}
                initialData={this.state.selectedData}
                srcSystems={this.props.srcSystems}
                matchRules={this.state.matchRules}
                matchRulesLoading={this.state.matchRulesLoading}
              />
            )}
            {!this.state.selectedData && (
              <div className="dmt-empty"><div className="dmt-empty-icon">⬅</div><div className="dmt-empty-label">Select a field from the tree to view or edit its properties</div></div>
            )}
          </div>
        </div>

        <AddFieldModal show={this.state.showAddFieldModal} showNested={true} handleClose={this.toggleAddFieldModal} handleSave={this.handleSaveAddField}/>
        {this.renderEditModal(dmWithProfile[0], eDataSupportingData)}
      </div>
    );
  }
}

export default DataModelTree;